// eslint-disable-next-line lodash/import-scope
import type { DebouncedFunc } from "lodash"
import throttle from "lodash/throttle"

import { progressStore } from "../watermelon/stores/progress"
import analytics from "./analytics"
import { PROGRESS_UPDATE_INTERVAL } from "./av/constants"
import { PlaybackState, Track } from "./av/types"
import { debug } from "./logging"
import { playbackTimerStore } from "./playback-timer"
import { getProgressItemFromTrack, persistProgress } from "./progress-item"

const ANALYTICS_THROTTLE_MS = 60000

const debugTracker = debug.extend("playProgress:tracker")

/**
 * This class helps track engagement time for playable content. The logProgress
 * method should be called at least once every logProgressInterval milliseconds
 * while the content is playing. The setPlaybackState/Rate methods should be
 * called any time the playback state/rate changes. The tracker will use the
 * information from these calls to keep track of how long the user is "engaged"
 * with the playable content.
 */
export class PlayProgressTracker {
  private engagementTime = new Map<string, number>()
  private playTime = new Map<string, number>()
  private progressLoggedAt?: number
  private progressLoggedForKey?: string
  private playbackState: PlaybackState
  private playbackRate: number

  constructor({
    playbackRate,
    playbackState,
  }: {
    playbackRate: number
    playbackState: PlaybackState
  }) {
    this.playbackState = playbackState
    this.playbackRate = playbackRate
  }

  logProgress = (key: string) => {
    if (
      this.progressLoggedAt &&
      this.progressLoggedForKey === key &&
      this.playbackState === PlaybackState.Playing
    ) {
      // Cap the time at twice what we expect it to be. This allows for timing
      // variations due to slow devices or hiccups in the av engine while doing
      // a reasonable job of filtering out true tracking errors.
      const time = Math.min(
        performance.now() - this.progressLoggedAt,
        PROGRESS_UPDATE_INTERVAL * 2,
      )
      debugTracker("logProgress", { key, time })
      this.engagementTime.set(key, (this.engagementTime.get(key) ?? 0) + time)
      this.playTime.set(
        key,
        (this.playTime.get(key) ?? 0) + this.playbackRate * time,
      )
    }
    this.progressLoggedAt = performance.now()
    this.progressLoggedForKey = key
  }

  setPlaybackState = (playbackState: PlaybackState) => {
    // Log any outstanding progress before making changes
    if (this.progressLoggedForKey) {
      this.logProgress(this.progressLoggedForKey)
    }
    this.playbackState = playbackState
  }

  setPlaybackRate = (playbackRate: number) => {
    // Log any outstanding progress before making changes
    if (this.progressLoggedForKey) {
      this.logProgress(this.progressLoggedForKey)
    }
    this.playbackRate = playbackRate
  }

  getEngagementTime = (key: string) => {
    return this.engagementTime.get(key) ?? 0
  }

  getPlayTime = (key: string) => {
    return this.playTime.get(key) ?? 0
  }

  resetTime = (key: string) => {
    debugTracker("resetTime", { key })
    this.engagementTime.delete(key)
    this.playTime.delete(key)
  }
}

/**
 * This map holds throttled copies of analytics.logPlayProgress keyed by
 * progress item keys. This allows us to throttle calls to the analytics service
 * (which are expensive). We throttle per progress item to ensure we don't miss
 * progress events when switching tracks.
 */
const throttledAnalyticsLogPlayProgress = new Map<
  string,
  DebouncedFunc<typeof analytics.logPlayProgress>
>()

/**
 * This should be called at regular intervals (ideally once per second) during
 * playback of playable content.
 */
export async function handlePlayProgressEvent({
  position,
  duration,
  playbackRate,
  track,
  tracks,
  playProgressTracker,
  playMode,
  pause,
}: {
  position: number
  duration: number
  playbackRate: number
  track: Track | null
  tracks: Track[]
  playProgressTracker: PlayProgressTracker
  playMode: "watch" | "listen"
  pause?: () => Promise<void>
}) {
  // Ignore events where duration = 0 (we can't save progress if duration
  // hasn't loaded yet) or where position = 0 (there's no progress to
  // save...)
  if (!track || duration === 0 || position === 0) return

  const progressItem = getProgressItemFromTrack({
    track,
    position,
    duration,
    playbackRate,
    playMode,
  })
  const progressItemKey = progressItem.getKey()

  // Save progress
  persistProgress(progressItem)

  // Track progress for analytics
  playProgressTracker.logProgress(progressItemKey)

  // Log analytics
  let logPlayProgress = throttledAnalyticsLogPlayProgress.get(progressItemKey)
  if (!logPlayProgress) {
    logPlayProgress = throttle(
      analytics.logPlayProgress,
      ANALYTICS_THROTTLE_MS,
      {
        leading: true,
        trailing: true,
      },
    )
    throttledAnalyticsLogPlayProgress.set(progressItemKey, logPlayProgress)
  }

  logPlayProgress({
    tracks,
    progressItem,
    playProgressTracker,
  })

  // Don't keep finished items in the audio player
  if (progressItem.getProgress().finished) {
    const profileId = progressItem.getProfileId()
    const currentAudioProgressItemKey =
      await progressStore.getCurrentAudioProgressItemKey({ profileId })
    if (currentAudioProgressItemKey === progressItemKey) {
      await progressStore.clearCurrentAudioProgressItemKey({ profileId })
    }
  }

  // Handle an expiring playback timer
  if (pause) {
    void playbackTimerStore.pauseIfExpired(pause)
  }
}

export function handlePlaybackStateChanged({
  playbackState,
  playProgressTracker,
}: {
  playbackState: PlaybackState
  playProgressTracker: PlayProgressTracker
}) {
  playProgressTracker.setPlaybackState(playbackState)
  throttledAnalyticsLogPlayProgress.forEach((logger) => logger.flush())
}

export function handlePlaybackRateChanged({
  playbackRate,
  playProgressTracker,
}: {
  playbackRate: number
  playProgressTracker: PlayProgressTracker
}) {
  playProgressTracker.setPlaybackRate(playbackRate)
}

export function handleTracksChanged() {
  throttledAnalyticsLogPlayProgress.forEach((logger, key) => {
    logger.flush()
    throttledAnalyticsLogPlayProgress.delete(key)
  })
}
